/**
 * Convert a base64 string to a Blob
 * @param base64Content - The base64 encoded string
 * @param contentType - Optional MIME type for the blob (defaults to 'application/octet-stream')
 * @returns A Blob containing the decoded binary data
 */
export function base64ToBlob(
  base64Content: string,
  contentType: string = 'application/octet-stream'
): Blob {
  const binaryString = atob(base64Content);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: contentType });
}

/**
 * Convert a Blob to a base64 string
 * @param blob - The Blob to convert
 * @returns Promise resolving to the base64 encoded string (without data URL prefix)
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove data URL prefix if present (data:...;base64,)
      const base64Data = result.includes(',') ? result.split(',')[1] : result;
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert Electron file content to a Blob
 * Handles both binary (base64) and text content
 * @param fileResult - The result from Electron API readFile
 * @param defaultContentType - Optional default content type for text files (defaults to 'text/plain')
 * @returns A Blob containing the file content, or null if the file result is invalid
 */
export function convertElectronFileContentToBlob(
  fileResult: {
    success: boolean;
    content?: string;
    isBinary?: boolean;
    error?: string;
  },
  defaultContentType: string = 'text/plain'
): Blob | null {
  if (!fileResult.success || fileResult.content === undefined) {
    return null;
  }

  if (fileResult.isBinary) {
    // Content is base64 encoded binary data
    // Validate base64 format if needed
    const base64Content = fileResult.content;
    if (!/^[A-Za-z0-9+/=]+$/.test(base64Content)) {
      // If it doesn't look like base64, treat as text
      return new Blob([base64Content], { type: defaultContentType });
    }
    return base64ToBlob(base64Content, 'application/octet-stream');
  } else {
    // Content is text
    return new Blob([fileResult.content], { type: defaultContentType });
  }
}
